import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Check } from "lucide-react";

const REPORT_TYPES = [
  { v: "quarterly_portfolio_update", label: "Quarterly portfolio update", desc: "LP-ready review of operating, cash, runway for a portfolio company.", n: "01" },
  { v: "pe_investment_memo", label: "PE / VC investment memo", desc: "IC memo with deal structure, returns analysis, value-creation plan.", n: "02" },
  { v: "private_credit_memo", label: "Private credit memo", desc: "Underwriting memo for NBFIs and direct lenders — covenants, downside.", n: "03" },
  { v: "equity_research", label: "Equity research", desc: "Initiation, earnings or thematic note for listed coverage.", n: "04" },
];

export default function NewReport() {
  const nav = useNavigate();
  const fileInput = useRef(null);
  const [type, setType] = useState("quarterly_portfolio_update");
  const [form, setForm] = useState({ company_name: "", ticker: "", sector: "", additional_notes: "" });
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadDocs = async () => {
    const r = await api.get("/documents");
    setDocs(r.data);
  };
  useEffect(() => { loadDocs(); }, []);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setSelected((s) => [...s, r.data.id]);
        toast.success(`Uploaded ${r.data.filename}`);
      }
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/reports", { report_type: type, ...form, document_ids: selected });
      toast.success("Generating draft — usually 30–90 seconds");
      nav(`/app/reports/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-10 py-12" data-testid="new-report-page">
      <div className="flex items-center justify-between text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--warm-grey)] border-b border-[var(--ink)]/15 pb-3">
        <span>Workspace · New draft</span>
        <span>cosalpha</span>
      </div>

      <div className="smallcaps text-[var(--saffron)] mt-12">No. 03 — Compose</div>
      <h1 className="font-serif text-[48px] font-medium tracking-[-0.02em] mt-4">Create a report.</h1>
      <p className="text-[14px] text-[var(--ink)]/75 mt-3 max-w-[60ch]">Pick a framework, add deal context, attach source documents — quarterly MIS, CIMs, transcripts, models.</p>

      <form onSubmit={onSubmit} className="mt-12 space-y-12">
        {/* type */}
        <section>
          <div className="smallcaps text-[var(--warm-grey)] mb-5">01 · Report framework</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REPORT_TYPES.map((t) => {
              const active = type === t.v;
              return (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setType(t.v)}
                  className={`text-left p-6 transition-colors duration-200 border ${
                    active ? "bg-[var(--ink)] text-[var(--cream)] border-[var(--ink)]" : "bg-white border-[var(--ink)]/15 hover:border-[var(--ink)]"
                  }`}
                  data-testid={`report-type-${t.v}`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className={`font-mono text-[11px] tracking-[0.2em] ${active ? "text-[var(--saffron)]" : "text-[var(--warm-grey)]"}`}>{t.n}</span>
                    {active && <Check className="w-4 h-4 text-[var(--saffron)]" />}
                  </div>
                  <div className="font-serif text-[22px] font-medium mt-5 tracking-tight">{t.label}</div>
                  <div className={`text-[13px] mt-2 leading-[1.6] ${active ? "text-[var(--cream)]/80" : "text-[var(--ink)]/70"}`}>{t.desc}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* details */}
        <section>
          <div className="smallcaps text-[var(--warm-grey)] mb-5">02 · Deal context</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="smallcaps text-[var(--warm-grey)]">Company / borrower</label>
              <input
                required
                value={form.company_name}
                onChange={update("company_name")}
                className="mt-2 w-full bg-white border-b border-[var(--ink)]/30 px-1 py-3 focus:outline-none focus:border-[var(--ink)] transition-colors text-[16px] font-mono"
                placeholder="Indus Logistics Pvt. Ltd."
                data-testid="new-report-company-input"
              />
            </div>
            <div>
              <label className="smallcaps text-[var(--warm-grey)]">Ticker (if listed)</label>
              <input
                value={form.ticker}
                onChange={update("ticker")}
                className="mt-2 w-full bg-white border-b border-[var(--ink)]/30 px-1 py-3 focus:outline-none focus:border-[var(--ink)] transition-colors text-[16px] font-mono"
                placeholder="NSE: INDLOG"
                data-testid="new-report-ticker-input"
              />
            </div>
            <div>
              <label className="smallcaps text-[var(--warm-grey)]">Sector</label>
              <input
                value={form.sector}
                onChange={update("sector")}
                className="mt-2 w-full bg-white border-b border-[var(--ink)]/30 px-1 py-3 focus:outline-none focus:border-[var(--ink)] transition-colors text-[16px]"
                placeholder="Logistics · supply chain"
                data-testid="new-report-sector-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="smallcaps text-[var(--warm-grey)]">Analyst notes / focus areas</label>
              <textarea
                rows={3}
                value={form.additional_notes}
                onChange={update("additional_notes")}
                className="mt-2 w-full bg-white border-b border-[var(--ink)]/30 px-1 py-3 focus:outline-none focus:border-[var(--ink)] transition-colors text-[14px]"
                placeholder="Flag margin trajectory post acquisition. Confirm covenant package assumptions."
                data-testid="new-report-notes-input"
              />
            </div>
          </div>
        </section>

        {/* documents */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div className="smallcaps text-[var(--warm-grey)]">03 · Source documents</div>
            <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--saffron)]">{selected.length} selected</div>
          </div>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
            className="bg-white border border-dashed border-[var(--ink)]/30 hover:border-[var(--ink)] p-10 text-center transition-colors"
            data-testid="new-report-dropzone"
          >
            <Upload className="w-5 h-5 mx-auto text-[var(--warm-grey)]" strokeWidth={1.5} />
            <div className="mt-4 font-serif text-[20px]">Drop PDFs here</div>
            <div className="text-[12px] text-[var(--warm-grey)] mt-1 tracking-[0.18em] uppercase font-semibold">— or —</div>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="mt-4 inline-flex items-center gap-2 bg-[var(--ink)] hover:bg-[var(--ink)]/90 text-[var(--cream)] px-5 py-2.5 text-[13px] tracking-wide transition-colors"
              data-testid="new-report-browse-button"
            >
              {uploading ? "Uploading…" : "Browse files"}
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept="application/pdf"
              className="hidden"
              onChange={(e) => onFiles(e.target.files)}
              data-testid="new-report-file-input"
            />
          </div>

          {docs.length > 0 && (
            <div className="mt-6 bg-white border border-[var(--ink)]/15">
              <div className="px-5 py-3 smallcaps text-[var(--warm-grey)] border-b border-[var(--ink)]/15">Library · select to include</div>
              <div className="max-h-72 overflow-auto">
                {docs.map((d) => {
                  const isSel = selected.includes(d.id);
                  return (
                    <button
                      type="button"
                      key={d.id}
                      onClick={() => toggle(d.id)}
                      className={`w-full flex items-center justify-between px-5 py-3.5 border-b last:border-b-0 border-[var(--ink)]/10 text-left transition-colors ${isSel ? "bg-[var(--saffron)]/10" : "hover:bg-[var(--ink)]/4"}`}
                      data-testid={`doc-select-${d.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-4 h-4 text-[var(--warm-grey)] shrink-0" strokeWidth={1.5} />
                        <div className="min-w-0">
                          <div className="font-serif text-[15px] truncate">{d.filename}</div>
                          <div className="text-[11px] text-[var(--warm-grey)] tabular">{d.pages} pages · {(d.size / 1024).toFixed(0)} KB {d.has_text ? "" : "· no text extracted"}</div>
                        </div>
                      </div>
                      <div className={`w-4 h-4 border flex items-center justify-center ${isSel ? "bg-[var(--saffron)] border-[var(--saffron)]" : "border-[var(--ink)]/40"}`}>
                        {isSel && <Check className="w-3 h-3 text-[var(--ink)]" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        <div className="flex items-center justify-end gap-4 border-t border-[var(--ink)]/15 pt-8">
          <button
            type="button"
            onClick={() => nav("/app/reports")}
            className="px-6 py-3 text-[13px] tracking-wide text-[var(--ink)] hover:underline underline-offset-[5px] decoration-[var(--saffron)] decoration-2"
            data-testid="new-report-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-7 py-3.5 text-[14px] tracking-wide bg-[var(--ink)] hover:bg-[var(--ink)]/90 text-[var(--cream)] transition-colors disabled:opacity-60"
            data-testid="new-report-submit"
          >
            {submitting ? "Generating…" : "Generate draft"}
          </button>
        </div>
      </form>
    </div>
  );
}
